import { TextareaHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ 
    className, 
    label, 
    error, 
    helperText, 
    id, 
    ...props 
  }, ref) => {
    const { isRTL } = useTranslation()
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`
    
    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={textareaId} 
            className="block text-sm text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-danger-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          <textarea
            id={textareaId}
            className={clsx(
              'textarea',
              error && 'input-error',
              className
            )}
            ref={ref}
            {...props}
          />
          
          {error && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-start pt-3 pointer-events-none`}>
              <AlertCircle className="w-5 h-5 text-danger-500" />
            </div>
          )}
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <AlertCircle className="w-4 h-4 text-danger-500 flex-shrink-0" />
            <p className="text-sm text-danger-600 font-medium">{error}</p>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-secondary-500">{helperText}</p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

export default Textarea

